import { Controller } from "@hotwired/stimulus";
import * as luxon from "luxon";

export default class extends Controller {
  static targets = [
    "packageOption",
    "programContainer",
    "programCheckbox",
    "programOptions",
    "enterCodeContainer",
    "codeField",
    "validationBlock",
    "validationMessage"
  ];

  static values = {
    pupilDob: String,
    timeZone: String
  };

  connect() {
    if (this.hasProgramCheckboxTarget) {
      this.programCheckboxTargets.forEach(checkbox => {
        if (checkbox.checked) {
          const programId = checkbox.dataset.programId;
          const programContainer = checkbox.closest('[data-driving-school-student-action-target="programContainer"]');
          if (programContainer) {
            const programOptions = programContainer.querySelector(
              `[data-driving-school-student-action-target="programOptions"][data-program-id="${programId}"]`
            );
            if (programOptions) {
              programOptions.classList.remove('u-hidden');
              programOptions.hidden = false;
            }
          }
        }
      });
    }
  }

  assignPackage(event, payLater = false) {
    event.currentTarget.disabled = true;

    const selectedPackageElement = this.packageOptionTargets.find(
      (element) => element.querySelector('input[type="radio"]').checked
    );
    const packageId = selectedPackageElement.dataset.packageId;
    const schoolsNavigationToken = document.querySelector("#school_navigation_token").value;

    let request_url = `/schools/pupils/${schoolsNavigationToken}/actions/assign-package/${packageId}`;
    if (payLater) {
      request_url += "?pay_later=true";
    }

    const debug = new URLSearchParams(window.location.search).get("debug");
    if (debug) {
      request_url += (request_url.includes('?') ? '&' : '?') + `debug=${debug}`;
    }

    const success_url = `/schools/pupils/${schoolsNavigationToken}/actions?package_assigned=true`;

    this.sendRequest(request_url, success_url);
  }

  payLaterForPackage(event) {
    this.assignPackage(event, true);
  }

  sendRequest(request_url, success_url) {
    fetch(request_url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.getMetaValue("csrf-token"),
        Accept: "text/html",
      },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((html) => {
        window.location.href = success_url;
      })
      .catch((error) => {
        console.error('Error:', error);
        // remove the packageId from the URL
        let redirect_url = window.location.href.split("?")[0];
        window.location.href = redirect_url + "?error=true";
      });
  }


  getMetaValue(name) {
    return document.querySelector(`meta[name="${name}"]`).getAttribute("content");
  }

  getMetaValue(name) {
    const element = document.head.querySelector(`meta[name="${name}"]`);
    return element.getAttribute("content");
  }

  async showAssignStudentProgramsQuickLook(event) {
    event.preventDefault();
    
    const url = event.target.href;
    const quickViewElement = document.querySelector("#student-action-quick-look");
    if (quickViewElement) {
      quickViewElement.classList.remove("u-hidden");
    }
    
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        }
      });
      
      if (response.ok) {
        const responseBody = await response.text();
        Turbo.renderStreamMessage(responseBody);
      }
    } catch (error) {
      console.error("Error loading quick look:", error);
    }
  }

  closeAssignStudentProgramsQuickLook(event) {
    event.preventDefault();
    const quickViewElement = document.querySelector("#student-action-quick-look");
    if (quickViewElement) {
      quickViewElement.classList.add("u-hidden");
    }
    
    const turboFrame = document.querySelector("#student_action_quick_look");
    if (turboFrame) {
      turboFrame.innerHTML = "";
    }
  }

  showConfirmAccountManuallyQuickLook(event) {
    event.preventDefault();
    const quickViewElement = document.querySelector("#student-action-confirm-account-quick-look");
    if (quickViewElement) {
      quickViewElement.classList.remove("u-hidden");
    }
  }

  closeConfirmAccountManuallyQuickLook(event) {
    event.preventDefault();
    const quickViewElement = document.querySelector("#student-action-confirm-account-quick-look");
    if (quickViewElement) {
      quickViewElement.classList.add("u-hidden");
    }
  }

  async submitStudentPrograms(event) {
    event.preventDefault();

    this.hideValidationBlock();

    const validationErrors = this.validate();

    if (validationErrors.length > 0) {
      this.showValidationBlock(validationErrors.join("<br>"));
      return;
    }
    
    const form = event.currentTarget;
    const formData = new FormData(form);
    const url = form.action || window.location.href;
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: formData
      });
      
      if (response.ok) {
        const responseBody = await response.text();
        Turbo.renderStreamMessage(responseBody);
      } else {
        const responseBody = await response.text();
        Turbo.renderStreamMessage(responseBody);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  }

  toggleProgramOptions(event) {
    const checkbox = event.target;
    const programId = checkbox.dataset.programId;
    const programContainer = checkbox.closest('[data-driving-school-student-action-target="programContainer"]');
    
    if (!programContainer) return;
    
    const programOptions = programContainer.querySelector(
      `[data-driving-school-student-action-target="programOptions"][data-program-id="${programId}"]`
    );
    
    if (!programOptions) return;
    
    if (checkbox.checked) {
      programOptions.classList.remove('u-hidden');
      programOptions.hidden = false;
    } else {
      programOptions.classList.add('u-hidden');
      programOptions.hidden = true;
      
      const financialScholarshipCheckbox = programOptions.querySelector(
        'input[type="checkbox"][name*="has_financial_scholarship"]'
      );
      if (financialScholarshipCheckbox) {
        financialScholarshipCheckbox.checked = false;
      }
      
      const codeField = programOptions.querySelector(
        '[data-driving-school-student-action-target="codeField"]'
      );
      if (codeField) {
        codeField.value = '';
      }
      
      const codeContainer = programOptions.querySelector(
        '[data-driving-school-student-action-target="enterCodeContainer"]'
      );
      if (codeContainer) {
        codeContainer.classList.add('u-hidden');
        codeContainer.hidden = true;
      }
    }
  }

  toggleCodeField(event) {
    const financialScholarshipCheckbox = event.target;
    if (financialScholarshipCheckbox.type !== 'checkbox') return;
    
    const programId = financialScholarshipCheckbox.dataset.programId;
    const programContainer = financialScholarshipCheckbox.closest(
      '[data-driving-school-student-action-target="programContainer"]'
    );
    
    if (!programContainer) return;
    
    const codeContainer = programContainer.querySelector(
      `[data-driving-school-student-action-target="enterCodeContainer"][data-program-id="${programId}"]`
    );
    const codeField = programContainer.querySelector(
      `[data-driving-school-student-action-target="codeField"][data-program-id="${programId}"]`
    );
    
    if (!codeContainer) return;
    
    const checked = financialScholarshipCheckbox.checked;
    
    if (checked) {
      codeContainer.classList.remove('u-hidden');
      codeContainer.hidden = false;
    } else {
      codeContainer.classList.add('u-hidden');
      codeContainer.hidden = true;
      if (codeField) {
        codeField.value = '';
      }
    }
  }

  validate() {
    const errors = [];

    const timezone = this.timeZoneValue || "America/Los_Angeles";
    const today = luxon.DateTime.now().setZone(timezone);

    const dob = this.hasPupilDobValue ? luxon.DateTime.fromISO(this.pupilDobValue) : null;

    this.programCheckboxTargets.forEach(checkbox => {
      if (!checkbox.checked) return;

      const programContainer = checkbox.closest('[data-driving-school-student-action-target="programContainer"]');
      const programName = checkbox.labels[0]?.textContent.trim() || "Program";
      
      if (dob) {
        const age = today.diff(dob, ['years', 'months']).toObject();
        const currentTotalMonths = (Math.floor(age.years) * 12) + Math.floor(age.months);

        const minYears = parseInt(programContainer.dataset.minAgeYears) || 0;
        const minMonths = parseInt(programContainer.dataset.minAgeMonths) || 0;
        const maxYears = parseInt(programContainer.dataset.maxAgeYears) || 18;
        const maxMonths = parseInt(programContainer.dataset.maxAgeMonths) || 0;

        const minTotalMonths = (minYears * 12) + minMonths;
        const maxTotalMonths = (maxYears * 12) + maxMonths;

        if (currentTotalMonths < minTotalMonths || currentTotalMonths > maxTotalMonths) {
          const minText = minMonths > 0 ? `${minYears} years ${minMonths} months` : `${minYears} years`;
          const maxText = maxMonths > 0 ? `${maxYears} years ${maxMonths} months` : `${maxYears} years`;
          
          errors.push(`<strong class="u-textRedMid">${programName}</strong>: Student must be ${minText} - ${maxText}`);
        }
      }

      const scholarshipCheckbox = programContainer.querySelector('input[type="checkbox"][name*="has_financial_scholarship"]');
      if (scholarshipCheckbox?.checked) {
        const codeField = programContainer.querySelector('[data-driving-school-student-action-target="codeField"]');
        if (codeField) {
          const code = codeField.value.trim();
          const requiredLength = parseInt(codeField.dataset.codeLength || '0');
          if (code !== '' && requiredLength > 0 && code.length !== requiredLength) {
            errors.push(`<strong class="u-textRedMid">${programName}</strong>: Scholarship code must be ${requiredLength} characters.`);
          }
        }
      }
    });

    return errors;
  }

  showValidationBlock(message) {
    this.validationMessageTarget.innerHTML = message;
    this.validationBlockTarget.hidden = false;
    this.validationBlockTarget.classList.remove('u-hidden');
  }

  hideValidationBlock() {
    this.validationBlockTarget.hidden = true;
    this.validationBlockTarget.classList.add('u-hidden');
  }
}
